import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Loader2, Pencil, Plus, Search, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBanner,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  Textarea,
  toast,
} from "@lumanity/ui/primitives";
import { useAdminSectionPrompts, useSectionPromptMutations } from "../draftApi";
import type { AdminSectionPrompt, SectionPromptInput } from "../types";

// Laid out like heor.pico.frontend's prompt library (Admin → Prompts): filter bar,
// sortable columns, toggle-to-activate, and its Add/Edit Prompt and delete dialogs.

type SortColumn = "name" | "prompt" | "status";
type SortState = { column: SortColumn | null; dir: "asc" | "desc" | null };

// As the library's MasterDataTable pages its rows (its paginate helper isn't exported).
const PAGE_SIZE = 10;

const comparators: Record<SortColumn, (a: AdminSectionPrompt, b: AdminSectionPrompt) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  prompt: (a, b) => a.text.localeCompare(b.text),
  status: (a, b) => Number(b.is_active) - Number(a.is_active),
};

const PromptFormDialog = ({
  open,
  onOpenChange,
  editing,
  onSubmit,
  isSubmitting,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: AdminSectionPrompt | null;
  onSubmit: (values: SectionPromptInput) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priming, setPriming] = useState("");

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? "");
      setDescription(editing?.description ?? "");
      setPriming(editing?.text ?? "");
    }
  }, [open, editing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !priming.trim()) return;
    await onSubmit({
      name: name.trim(),
      description: description.trim() || null,
      text: priming.trim(),
      is_active: editing?.is_active ?? true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !isSubmitting && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Prompt" : "Add Prompt"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="prompt-name" className="font-semibold">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="prompt-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter prompt name"
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt-description">Description</Label>
              <Textarea
                id="prompt-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter when writers should use this prompt, shown in the library picker (optional)"
                disabled={isSubmitting}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt-priming" className="font-semibold">
                Priming (System Prompt) <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="prompt-priming"
                value={priming}
                onChange={(e) => setPriming(e.target.value)}
                placeholder="Enter the AI instructions a section gets when a writer picks this prompt"
                disabled={isSubmitting}
                rows={6}
              />
            </div>

            {error && <ErrorBanner message={error} />}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !name.trim() || !priming.trim()} className="bg-brand-teal">
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DeletePromptDialog = ({
  prompt,
  isDeleting,
  onConfirm,
  onCancel,
}: {
  prompt: AdminSectionPrompt | null;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Dialog open={!!prompt} onOpenChange={(o) => !o && !isDeleting && onCancel()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="w-5 h-5" /> Delete Prompt
        </DialogTitle>
        <DialogDescription>This will permanently delete this prompt. This action cannot be undone.</DialogDescription>
      </DialogHeader>
      {prompt && <p className="text-sm font-medium text-foreground break-words">{prompt.name}</p>}
      <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        Sections that already use it keep their own copy of the instructions.
      </p>
      <div className="flex justify-end gap-3 mt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button type="button" variant="destructive" onClick={onConfirm} disabled={isDeleting} className="gap-1.5">
          {isDeleting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Deleting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4" /> Delete
            </>
          )}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

/**
 * Section prompts (stored in the prompt library, as in PICO): the AI instructions writers
 * can "choose from library" for a First draft section. Picking one copies its text, so
 * edits here never change existing sections.
 */
const SectionPromptsAdmin = () => {
  const { data: prompts = [], isLoading, error } = useAdminSectionPrompts();
  const { create, update, remove } = useSectionPromptMutations();
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sort, setSort] = useState<SortState>({ column: null, dir: null });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminSectionPrompt | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AdminSectionPrompt | null>(null);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const shown = prompts.filter(
      (p) => (!q || `${p.name} ${p.text}`.toLowerCase().includes(q)) && (filterStatus === "all" || (filterStatus === "active") === p.is_active),
    );
    const compare = sort.column ? comparators[sort.column] : comparators.name;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...shown].sort((a, b) => dir * compare(a, b));
  }, [prompts, searchText, filterStatus, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Same column: asc → desc → unsorted; a new column starts ascending.
  const cycleSort = (column: SortColumn) =>
    setSort((prev) => {
      if (prev.column !== column) return { column, dir: "asc" };
      if (prev.dir === "asc") return { column, dir: "desc" };
      return { column: null, dir: null };
    });

  const sortIcon = (column: SortColumn) => {
    const Icon = sort.column !== column ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    return <Icon className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  const sortableHead = (column: SortColumn, label: string, className = "") => (
    <TableHead className={`cursor-pointer select-none ${className}`} onClick={() => cycleSort(column)}>
      <div className="flex items-center gap-1">
        {label}
        {sortIcon(column)}
      </div>
    </TableHead>
  );

  const openDialog = (prompt: AdminSectionPrompt | null) => {
    setEditing(prompt);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (body: SectionPromptInput) => {
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync(body);
      toast({ title: editing ? "Prompt updated" : "Prompt added" });
      setDialogOpen(false);
      setEditing(null);
    } catch (e) {
      setDialogError((e as Error).message);
    }
  };

  const handleToggleActive = (prompt: AdminSectionPrompt) => {
    update.mutate(
      {
        id: prompt.id,
        body: {
          name: prompt.name,
          description: prompt.description,
          text: prompt.text,
          is_active: !prompt.is_active,
        },
      },
      {
        onError: (e) =>
          toast({
            title: "Could not update the prompt",
            description: e.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onError: (e) =>
        toast({
          title: "Could not delete the prompt",
          description: e.message,
          variant: "destructive",
        }),
      onSettled: () => setDeleting(null),
    });
  };

  if (error) return <ErrorBanner message={`Could not load the prompt library: ${error.message}`} />;

  return (
    <>
      <div className="space-y-4">
        {/* Filter Block */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search prompts..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              className="pl-8"
            />
          </div>
          <Select
            value={filterStatus}
            onValueChange={(v) => {
              setFilterStatus(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => openDialog(null)} size="sm" className="bg-brand-teal">
            <Plus className="w-4 h-4 mr-2" />
            Add Prompt
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No prompts found. Click "Add Prompt" to create one.</div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  {sortableHead("name", "Name")}
                  {sortableHead("prompt", "Prompt")}
                  {sortableHead("status", "Status", "w-[100px]")}
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((prompt) => (
                  <TableRow key={prompt.id}>
                    <TableCell className="font-medium">{prompt.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs">
                      {prompt.text ? (
                        <span className="block truncate" title={prompt.text}>
                          {prompt.text}
                        </span>
                      ) : (
                        <span className="italic">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={prompt.is_active ? "default" : "secondary"}>{prompt.is_active ? "Active" : "Inactive"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openDialog(prompt)} title="Edit" aria-label={`Edit ${prompt.name}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(prompt)}
                          title={prompt.is_active ? "Deactivate" : "Activate"}
                          aria-label={`${prompt.is_active ? "Deactivate" : "Activate"} ${prompt.name}`}
                        >
                          {prompt.is_active ? (
                            <ToggleRight className="w-4 h-4 text-brand-teal" />
                          ) : (
                            <ToggleLeft className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleting(prompt)}
                          title="Delete"
                          aria-label={`Delete ${prompt.name}`}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <TablePagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
      </div>

      <PromptFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        editing={editing}
        onSubmit={handleSubmit}
        isSubmitting={create.isPending || update.isPending}
        error={dialogError}
      />

      <DeletePromptDialog prompt={deleting} isDeleting={remove.isPending} onConfirm={handleDeleteConfirm} onCancel={() => setDeleting(null)} />
    </>
  );
};

export default SectionPromptsAdmin;
